import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { catchError, map, of, tap } from 'rxjs';

/**
 * Inlines an external SVG into the DOM so inherited CSS (like `currentColor`)
 * resolves against the current document context.
 *
 * - `src`   : path to the SVG asset.
 * - `color` : CSS color applied to the host's `color` property; any element
 *             inside the SVG that uses `fill="currentColor"` adopts this.
 *             Defaults to the theme's `--p-primary-600`.
 *
 * SVG responses are cached at the class level so the same asset is fetched
 * only once per session.
 */
@Component({
  selector: 'm-svg',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `<div class="svg-host" [innerHTML]="safeSvg()"></div>`,
  host: {
    '[style.color]': 'color()',
    '[style.aspect-ratio]': 'aspectRatio()',
  },
  styles: [
    `
      :host {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        line-height: 0;
        overflow: hidden;
      }

      .svg-host {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        min-width: 0;
        min-height: 0;
      }

      .svg-host ::ng-deep svg {
        display: block;
        width: 100%;
        height: 100%;
        max-width: 100%;
        max-height: 100%;
      }
    `,
  ],
})
export class ThemedSvgComponent {
  private readonly http = inject(HttpClient);
  private readonly sanitizer = inject(DomSanitizer);

  private static readonly cache = new Map<string, NormalizedSvg>();
  private static readonly inflight = new Map<string, Promise<NormalizedSvg>>();

  readonly src = input.required<string>();
  readonly color = input<string>('var(--p-primary-600)');

  private readonly raw = signal<string>('');
  readonly aspectRatio = signal<string | null>(null);

  readonly safeSvg = computed<SafeHtml>(() =>
    this.sanitizer.bypassSecurityTrustHtml(this.raw()),
  );

  constructor() {
    effect(() => this.load(this.src()));
  }

  private load(url: string): void {
    if (!url) {
      this.raw.set('');
      this.aspectRatio.set(null);
      return;
    }

    const cached = ThemedSvgComponent.cache.get(url);
    if (cached !== undefined) {
      this.apply(cached);
      return;
    }

    const existing = ThemedSvgComponent.inflight.get(url);
    if (existing) {
      existing.then(svg => this.apply(svg));
      return;
    }

    const promise = new Promise<NormalizedSvg>(resolve => {
      this.http
        .get(url, { responseType: 'text' })
        .pipe(
          map(content => ThemedSvgComponent.normalize(content)),
          tap(svg => ThemedSvgComponent.cache.set(url, svg)),
          catchError(() => of<NormalizedSvg>({ html: '', aspect: null })),
        )
        .subscribe(svg => {
          ThemedSvgComponent.inflight.delete(url);
          resolve(svg);
          this.apply(svg);
        });
    });

    ThemedSvgComponent.inflight.set(url, promise);
  }

  private apply(svg: NormalizedSvg): void {
    this.raw.set(svg.html);
    this.aspectRatio.set(svg.aspect);
  }

  /**
   * Strips the SVG's intrinsic `width`/`height` so it can be sized purely by
   * CSS, and extracts the `viewBox` aspect ratio so the host element can
   * mimic a replaced element (giving consumers `<img>`-like sizing behavior).
   */
  private static normalize(content: string): NormalizedSvg {
    const html = content.replace(/<svg\b([^>]*)>/i, (_, rawAttrs: string) => {
      const cleaned = rawAttrs
        .replace(/\s(?:width|height)\s*=\s*"[^"]*"/gi, '')
        .replace(/\s(?:width|height)\s*=\s*'[^']*'/gi, '');
      return `<svg${cleaned}>`;
    });

    const viewBoxMatch = content.match(
      /<svg\b[^>]*\sviewBox\s*=\s*["']\s*[-\d.]+\s+[-\d.]+\s+([\d.]+)\s+([\d.]+)\s*["']/i,
    );
    const aspect = viewBoxMatch
      ? `${viewBoxMatch[1]} / ${viewBoxMatch[2]}`
      : null;

    return { html, aspect };
  }
}

interface NormalizedSvg {
  readonly html: string;
  readonly aspect: string | null;
}
